// src/packages/care/prediction/menstrualBleeding.ts
//
// The bridge between what the Care check-in writes and what the fertile-window
// model can read.
//
// The check-in has no period-log endpoint behind it: a bleeding level is saved
// as a symptom row whose `symptom` field is the level's label. Those rows are
// the only record of when a patient bled, so they are also the only place cycle
// statistics can come from — but a label is a loose contract and a timestamp
// format is a looser one, so both live here, named and tested, instead of as
// string handling inside a screen.

/**
 * The exact label the check-in writes for each bleeding level.
 *
 * `bleedingOptions` in the Care screen builds its options from these, so the
 * string that is written and the string that is read back cannot drift apart.
 */
export const BLEEDING_SYMPTOM_LABELS = {
  spotting: "Spotting",
  light: "Light bleeding",
  medium: "Medium bleeding",
  heavy: "Heavy bleeding",
} as const;

/**
 * Labels earlier symptom pickers wrote for the same thing.
 *
 * `DailyCheckInSection` and `SymptomTracker` offered a "Bleeding & Clotting"
 * group of `["Spotting", "Light", "Heavy", "Clots"]`. Neither is mounted by any
 * screen today, but rows they wrote are in patients' records, and a record does
 * not stop being true because the surface that wrote it was retired. Reading
 * only the current vocabulary would make that history silently uncountable —
 * the exact failure this module exists to prevent.
 *
 * "Clots" is deliberately absent: clotting accompanies menstruation but is
 * logged as a separate observation, and a day marked only "Clots" is not
 * evidence of the day bleeding began.
 */
export const LEGACY_BLEEDING_LEVEL: Readonly<Record<string, string>> = {
  Light: "light",
  Heavy: "heavy",
};

const LEGACY_MENSTRUAL_LABELS: readonly string[] = Object.keys(
  LEGACY_BLEEDING_LEVEL,
);

/**
 * The levels that count as menstruation when deriving cycle starts.
 *
 * Spotting is deliberately absent. It is not a period, and mid-cycle spotting
 * counted as a period start would put a false cycle boundary a week or two
 * after the real one — the model would read two short cycles where there was
 * one normal cycle, and place the fertile window on the strength of it. A
 * silent estimate is a worse answer than an honest "not enough history".
 *
 * A spotting-only day still draws a period marker on the calendar, which the
 * check-in has always done. The screen says a day had bleeding; this list says
 * which bleeding a cycle can be measured from. They are different claims.
 */
export const MENSTRUAL_BLEEDING_LABELS: readonly string[] = [
  BLEEDING_SYMPTOM_LABELS.light,
  BLEEDING_SYMPTOM_LABELS.medium,
  BLEEDING_SYMPTOM_LABELS.heavy,
  ...LEGACY_MENSTRUAL_LABELS,
];

const MENSTRUAL_LABEL_SET = new Set<string>(MENSTRUAL_BLEEDING_LABELS);

/**
 * The current label for a stored one.
 *
 * A surface that offers the canonical vocabulary has to recognise the retired
 * one in its own records, or a row stored as "Heavy" will not match the "Heavy
 * bleeding" chip: it renders unselected, and toggling it writes a duplicate
 * instead of clearing the original. Anything keyed by the stored string —
 * selected state, id lookups for deletion — should key on this instead.
 */
export function canonicalBleedingLabel(stored: string | null | undefined) {
  if (!stored) return "";
  const level = LEGACY_BLEEDING_LEVEL[stored.trim()];
  return level
    ? BLEEDING_SYMPTOM_LABELS[level as keyof typeof BLEEDING_SYMPTOM_LABELS]
    : stored;
}

/**
 * The calendar day a stored timestamp refers to.
 *
 * The API returns `dateAndTime` as a Postgres timestamp —
 * `"2026-06-04 07:35:09.116+00"`, separated by a space, not the `T` an ISO
 * string uses. Splitting on `"T"` therefore returned the whole string and every
 * bleeding row was silently discarded, which is why a patient could log three
 * cycles and be told there was not enough history.
 *
 * The date part is read literally, never through `new Date()`. `useCreateSymptoms`
 * composes the row as the patient's selected local date plus the current UTC
 * time of day, so the leading date IS the day she picked; re-interpreting the
 * timestamp in a time zone would move a 23:00 entry to the following day for
 * anyone east of UTC, and silently shift her cycle boundaries.
 */
export function calendarDayOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/.exec(value.trim());
  return match?.[1] ?? null;
}

/** A symptom row, narrowed to the two fields this mapping reads. */
export type BleedingSymptomRecord = {
  symptom?: string | null;
  dateAndTime?: string | null;
};

/**
 * The days a patient recorded menstrual bleeding, ascending and de-duplicated.
 *
 * One day per date, never a range: a check-in says this day had bleeding and
 * implies nothing about the days around it. Grouping consecutive days into a
 * single period is `extractPeriodBlocks`' job downstream, which is what keeps a
 * five-day period counting as one cycle start rather than five.
 */
export function periodDaysFromBleedingSymptoms(
  records: readonly BleedingSymptomRecord[] | null | undefined,
): string[] {
  const days = new Set<string>();

  for (const record of records ?? []) {
    if (!record?.symptom || !MENSTRUAL_LABEL_SET.has(record.symptom.trim())) {
      continue;
    }
    const day = calendarDayOf(record.dateAndTime);
    if (day) days.add(day);
  }

  return [...days].sort((a, b) => a.localeCompare(b));
}
